import {useMemo, useState} from "react";
import baseUrl from "../../../../baseUrl";
import {BsEraserFill, BsFileText, BsFillTrash2Fill, BsLink45Deg} from "react-icons/bs";
import {requester} from "../../../../services/requester";
import {useAppContext} from "../../../../contexts/AppContext";
import {buildSettlementOptions, ekatteFromValue, valueForEkatte} from "../../../../utils/settlements";
import {ApprovalButtons, StatusNote, rowTint} from "../DealApproval";
import {useConfirm} from "../../../../contexts/ConfirmContext";

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

export const DealInfo = ({
                             user,
                             deal_id,
                             amount,
                             settlement_other,
                             company_other,
                             protocol_url,
                             bullstat_giving,
                             bullstat_receiving,
                             settlement_giving,
                             settlement_receiving,
                             year,
                             create_on,
                             settlement_ekatte,
                             approval_status,
                             rejected_by,
                             deleted_by,
                             author_ack,
                             onDealDelete
                         }) => {
    const request = requester();
    const confirm = useConfirm();
    const {settlements, user: currentUser} = useAppContext();

    // ЧАСТ К: редът се вижда и от двете страни на замяната. „Насрещна страна"
    // е тази, чието землище е settlement_receiving — там стои решението.
    const isFarSide = approval_status && approval_status !== 'not_required'
        && settlement_ekatte === settlement_receiving;
    const isAuthor = currentUser?.username === user;
    const isDeletedFar = approval_status === 'deleted_far';
    const settlementOptions = useMemo(() => buildSettlementOptions(settlements), [settlements]);
    const [showEdit, setShowEdit] = useState(false);
    const [dealAmount, setDealAmount] = useState(Math.abs(amount));
    // Е6: землищата при редакция (предпопълнени при отваряне).
    const [editSG, setEditSG] = useState('');
    const [editSR, setEditSR] = useState('');

    const попълниОтЗаписаното = () => {
        setDealAmount(Math.abs(amount));
        setEditSG(valueForEkatte(settlements, settlement_giving));
        setEditSR(valueForEkatte(settlements, settlement_receiving));
    };

    const toggleShowEdit = () => {
        if (!showEdit) {
            попълниОтЗаписаното();
        }
        setShowEdit(!showEdit);
    };

    // Ш1: изход без запис. Полетата се връщат към записаното, за да не заварят
    // половин промяна при следващото отваряне.
    const затвори = () => {
        попълниОтЗаписаното();
        setShowEdit(false);
    };

    const onClickDeleteHandle = async () => {
        // Р7: същото потвърждение като навсякъде (contexts/ConfirmContext).
        // Досега тук се отваряше втори ред „Действително ли желаеш…" — работеше,
        // но беше различен навик от останалите екрани.
        if (!await confirm({
            title: "Да се изтрие ли тази доброволна замяна?",
            message: "Изтрива се ЦЯЛАТА замяна — и при двамата ползватели, "
                + "и в двете землища.",
            detail: `${company_other?.company_name || ""} · `
                + `${Math.abs(amount).toFixed(2)}`,
        })) return;

        request.delete(`${baseUrl()}/deals/${deal_id}`)
            .then(() => {
                onDealDelete();
            })
            .catch(err => alert(err.message || 'Грешка при изтриване.'));
    };

    const changeEditHadler = (e) => {
        setDealAmount(e.target.value)
    }

    const onClickEditHandle = async () => {
        const sg = ekatteFromValue(editSG);
        const sr = ekatteFromValue(editSR);
        if (!sg || !sr) {
            alert('Изберете и двете землища.');
            return;
        }
        try {
            await request.put(`${baseUrl()}/deals/${deal_id}`, {
                amount: Number(Math.abs(dealAmount)),
                settlement_giving: sg,
                settlement_receiving: sr,
            });
            onDealDelete();
            toggleShowEdit();
        } catch (err) {
            alert(err.message || 'Редакцията не успя.');
        }
    };

    const generatePdfReport = async () => {
        const body = {
            bullstat_giving: Number(bullstat_giving),
            bullstat_receiving: Number(bullstat_receiving),
            settlement_giving: settlement_giving,
            settlement_receiving: settlement_receiving,
            amount: Number(Math.abs(dealAmount)),
        };

        try {
            await request.post(`${baseUrl()}/deals/pdf/${deal_id}`, body);
            onDealDelete();
        } catch (err) {
            alert(err.message || 'Възникна грешка при генерирането на протокола.');
        }
    }

    return (
        <>
            <tr style={{
                background: rowTint(approval_status),
                // Изтрита отсреща — редът е блед и неактивен, но ОСТАВА в
                // сметките на автора, докато той сам не я изтрие.
                opacity: isDeletedFar ? 0.55 : undefined,
            }}>
                <td className="text-center" style={{paddingTop: "14px"}}>{formatDate(create_on)}</td>
                <td className="text-center" style={{paddingTop: "14px"}}>{user}</td>
                <td className="text-center" style={{paddingTop: "14px"}}>{amount?.toFixed(2)}</td>
                <td className="text-center" style={{paddingTop: "14px"}}>
                    {settlement_other.settlement}
                    <StatusNote status={approval_status} isFarSide={isFarSide}
                                farSettlementName={settlement_other.settlement}
                                rejectedBy={rejected_by} deletedBy={deleted_by}
                                authorAck={author_ack}/>
                </td>
                <td className="text-center" style={{paddingTop: "14px"}}>{company_other.company_name}</td>
                <td>
                    <ApprovalButtons dealId={deal_id} status={approval_status}
                                     authorAck={author_ack} isFarSide={isFarSide}
                                     isAuthor={isAuthor} onChanged={onDealDelete}/>
                </td>
                <td>
                    <button onClick={toggleShowEdit} data-bs-toggle="tooltip" title="Редакция" type="button"
                            className="btn btn-warning"><BsEraserFill/></button>
                </td>
                <td>
                    <button onClick={onClickDeleteHandle} data-bs-toggle="tooltip" title="Изтриване" type="button"
                            className="btn btn-danger"><BsFillTrash2Fill/></button>
                </td>
                {protocol_url ? (
                    <td>
                        <a href={`${baseUrl()}${protocol_url}`} target="_blank" rel="noopener noreferrer"
                           className="btn btn-success" data-bs-toggle="tooltip" title="Линк за изтегляне на протокола"
                           style={{color: "white"}}><BsLink45Deg/></a>
                    </td>
                ) : (
                    <td>
                        <button onClick={generatePdfReport} type="button" data-bs-toggle="tooltip" title="Генериране на протокол"
                                className="btn btn-warning"><BsFileText/></button>
                    </td>
                )}

            </tr>

            {showEdit && (
                <tr style={{background: "var(--app-neutral-bg)"}}>
                    <td colSpan={8}>
                        <div className="d-flex flex-wrap gap-3 align-items-end p-2">
                            <div>
                                <label className="form-label mb-0" style={{fontSize: "0.85rem"}}>Сума (дка)</label>
                                <input onChange={changeEditHadler} type="number" step="0.01" min="0.01"
                                       className="form-control form-control-sm" style={{maxWidth: "120px"}}
                                       value={dealAmount} name="amount"/>
                            </div>
                            <div>
                                <label className="form-label mb-0" style={{fontSize: "0.85rem"}}>Дава от землище</label>
                                <input type="search" list={`edit-sg-${deal_id}`} value={editSG}
                                       onChange={e => setEditSG(e.target.value)}
                                       className="form-control form-control-sm" style={{maxWidth: "340px"}}/>
                                <datalist id={`edit-sg-${deal_id}`}>
                                    {settlementOptions.map(o => <option key={o.id} value={o.value}/>)}
                                </datalist>
                            </div>
                            <div>
                                <label className="form-label mb-0" style={{fontSize: "0.85rem"}}>Взима в землище</label>
                                <input type="search" list={`edit-sr-${deal_id}`} value={editSR}
                                       onChange={e => setEditSR(e.target.value)}
                                       className="form-control form-control-sm" style={{maxWidth: "340px"}}/>
                                <datalist id={`edit-sr-${deal_id}`}>
                                    {settlementOptions.map(o => <option key={o.id} value={o.value}/>)}
                                </datalist>
                            </div>
                            <button onClick={onClickEditHandle} type="button" className="btn btn-warning btn-sm">
                                Запази
                            </button>
                            {/* Ш1 */}
                            <button onClick={затвори} type="button"
                                    className="btn btn-outline-secondary btn-sm">
                                Затвори
                            </button>
                        </div>
                    </td>
                </tr>
            )}

        </>
    );
};